// Integração com a Mini App do Telegram (window.Telegram.WebApp).
// Fora do Telegram (navegador comum) tudo aqui vira no-op.

interface TgUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
}

interface TgWebApp {
  initData: string;
  initDataUnsafe: {
    user?: TgUser;
    start_param?: string;
    chat_instance?: string;
    chat_type?: string;
  };
  version: string;
  platform: string;
  isVersionAtLeast(version: string): boolean;
  ready(): void;
  expand(): void;
  requestFullscreen?(): void;
  disableVerticalSwipes?(): void;
  enableClosingConfirmation?(): void;
  showConfirm?(message: string, callback: (confirmed: boolean) => void): void;
  lockOrientation?(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  setBottomBarColor?(color: string): void;
  onEvent(event: string, handler: () => void): void;
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export interface TelegramContext {
  inTelegram: boolean;
  user: TgUser | null;
  /** Assinado pelo Telegram; o servidor valida com o token do bot. */
  initData: string;
  startParam: string | null;
  /** Identifica o chat de onde a Mini App foi aberta (só via link direto). */
  chatInstance: string | null;
  platform: string;
  version: string;
}

const webApp = (): TgWebApp | null => {
  const app = window.Telegram?.WebApp;
  return app && app.initData ? app : null;
};

export function initTelegram(): TelegramContext {
  const app = webApp();
  if (!app) {
    return {
      inTelegram: false,
      user: null,
      initData: '',
      startParam: null,
      chatInstance: null,
      platform: 'browser',
      version: '',
    };
  }

  app.ready();
  app.expand();
  app.setHeaderColor?.('#000000');
  app.setBackgroundColor?.('#000000');
  app.setBottomBarColor?.('#000000');

  // O gesto de arrastar para baixo fecha a Mini App e briga com o direcional.
  if (app.isVersionAtLeast('7.7')) app.disableVerticalSwipes?.();
  if (app.isVersionAtLeast('8.0')) {
    try {
      app.requestFullscreen?.();
    } catch {
      // Alguns clientes (ex.: Telegram Web) não suportam tela cheia.
    }
  }
  app.enableClosingConfirmation?.();

  return {
    inTelegram: true,
    user: app.initDataUnsafe.user ?? null,
    initData: app.initData,
    startParam: app.initDataUnsafe.start_param ?? null,
    chatInstance: app.initDataUnsafe.chat_instance ?? null,
    platform: app.platform,
    version: app.version,
  };
}

let orientationLocked = false;

/** Trava a Mini App na orientação atual (Bot API 8.0+). */
export function lockOrientation(): void {
  const app = webApp();
  if (orientationLocked || !app?.lockOrientation || !app.isVersionAtLeast('8.0')) return;
  try {
    app.lockOrientation();
    orientationLocked = true;
  } catch {
    // Telegram antigo ou sem suporte: segue sem travar.
  }
}

/** Pergunta sim/não (popup nativo do Telegram quando dá; senão o do navegador). */
export function confirmDialog(message: string): Promise<boolean> {
  const app = webApp();
  if (app?.showConfirm && app.isVersionAtLeast('6.2')) {
    return new Promise((resolve) => app.showConfirm!(message, resolve));
  }
  return Promise.resolve(window.confirm(message));
}

export function haptic(): void {
  try {
    webApp()?.HapticFeedback?.impactOccurred('light');
  } catch {
    // Feedback tátil é opcional.
  }
}

/** Chamado quando a Mini App é minimizada/restaurada (Bot API 8.0+) ou a aba muda. */
export function onAppVisibility(handler: (visible: boolean) => void): void {
  document.addEventListener('visibilitychange', () => handler(!document.hidden));
  const app = webApp();
  if (app?.isVersionAtLeast('8.0')) {
    app.onEvent('activated', () => handler(true));
    app.onEvent('deactivated', () => handler(false));
  }
}
